import "react-pp-bundle/dist/style.css"

import { CWILL_EVENT_NAMES } from "@channelwill/report"
import { isShopifyEmbedded } from "@shopify/app-bridge-utils"
import { Icon, Modal, Page } from "@shopify/polaris"
import { ChevronRightIcon } from "@shopify/polaris-icons"
import { Observer } from "mobx-react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { PBProvide } from "react-pp-bundle"
import { useLocation, useNavigate } from "react-router"

import accountApi from "@/api/account"
import { AppPricingInterval, BundleAppPricingInterval } from "@/api/account/type.d"
import { Container } from "@/component"
import { SwitchTab } from "@/component/SwitchTab"
import { SubscriptionPaymentMethodEnum } from "@/models/IUserInfo"
import BundlePage from "@/pages/Account/BundlePage"
import { ApplyDiscountCodeModal, useApplyDiscountCodeModal } from "@/pages/Account/components/ApplyDiscountCode"
import DescCards from "@/pages/Account/components/DescCards"
import PBAppToggleComponent from "@/pages/Account/components/PBAppToggleComponent"
import PlanList from "@/pages/Account/components/PlanList"
import { clearPathnameQuery } from "@/router/history"
import stores from "@/stores"
import toast from "@/utils/toast"

import AccountBanners from "./components/Banners"

const Account = () => {
  const { t } = useTranslation("account")
  const { t: commonT } = useTranslation("common")
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const { upgradeStore, userInfoSore } = stores
  const { isOpen, openModalAndInitDiscountCode, closeModal } = useApplyDiscountCodeModal()

  // 获取从 Billing 页面传递的 state
  const fromBillingFeatures = location.state?.fromBillingFeatures

  const updateCurrentSubscription = () => {
    accountApi.getCurrentSubscription()
      .then((res) => {
        const { current_discount } = userInfoSore.data

        upgradeStore.setCurrentPlan(res.data.current_subscription, current_discount ? current_discount.interval : undefined)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // const openUpgradeModal = () => setShowUpgradeModal(true)
  const openUpgradeModal = () => toast(t("UpgradedSuccessfully"))

  useEffect(() => {
    window.scrollTo({ top: 0 })
    setLoading(true)
    updateCurrentSubscription()
    // 如果没请求过plans，获取套餐列表
    if (!upgradeStore.initialized) upgradeStore.updatePlans()

    // 访问套餐页埋点
    try {
      if (window.isFromAdmin !== true && isShopifyEmbedded()) {
        window.cio && window.cio.trigger({
          event: "view_plan_page",
        })

        window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.VIEW_PLAN_PAGE)
      }
    } catch (e) {
      console.log(e)
    }


    upgradeStore.checkIsShowBundle()
    // upgradeStore.getBundleData()


    const { subscription_payment_method } = userInfoSore.data
    const isBundle = subscription_payment_method === SubscriptionPaymentMethodEnum.PARCELPANEL

    const toLevel = location.state?.level

    if (toLevel && upgradeStore){
      upgradeStore.setPreferKind(toLevel)
    }

    // 如果是从 Billing 页面"显示所有功能"跳转过来，强制设置为 parcelpanel
    const appType = fromBillingFeatures ? "parcelpanel" : (isBundle ? "bundle" : "parcelpanel")

    upgradeStore.setAppType(appType)

    function resetState() {
      upgradeStore.setAppType(isBundle ? "bundle" : "parcelpanel")
      upgradeStore.setBundlePayType(upgradeStore.currentPlan.interval === AppPricingInterval.Annual ? BundleAppPricingInterval.Annual : BundleAppPricingInterval.Every30_Days)
      upgradeStore.setPerferPlanId()
      upgradeStore.setPreferKind()
    }
    return resetState
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    // 降级套餐成功
    const charge_result = searchParams.get("charge_result")
    const source = searchParams.get("source")
    const subscribe_status = searchParams.get("subscribe_success")

    // bundle套餐的升降
    if (source === "3"){
      upgradeStore.setAppType("bundle")
      switch (subscribe_status) {
        // 升级成功
        case "1" :
          openUpgradeModal()
          break
        // 降级成功
        case "2" :
          clearPathnameQuery("/billing/plans")
          toast(t("DowngradedSuccessfully"))
          break
        default:
          break
      }
      return
    }


    switch (charge_result) {
      // 升级成功
      case "upgrade_success" :
        openUpgradeModal()
        break
      // 降级成功
      case "downgrade_success" :
        clearPathnameQuery("/billing/plans")
        toast(t("DowngradedSuccessfully"))
        break
      default:
        break
    }
  }, [location.search])

  return <Observer>{() =>
    <Container maxWidth={"1036px"} id="Page-Account">
      <ApplyDiscountCodeModal open={isOpen} onClose={closeModal} t={t} />

      <Page
        title={<div className={"flex items-center flex-wrap gap-y-2"}>
          <div className={"flex items-center"}>
            {t("ChangePlan")}
            {!!upgradeStore.showBannerData?.show_bundle && <Icon source={ChevronRightIcon} />}
          </div>
          {!!upgradeStore.showBannerData?.show_bundle &&
          <div className={"h-6 relative"}>
            <div className={"sm:absolute hidden sm:block top-1/2 z-50 transform translate-x-0 sm:-translate-y-1/2"} style={{  transform: "translate(var(--tw-translate-x), var(--tw-translate-y))" }}>
              <PBAppToggleComponent />
            </div>
          </div>
          }
        </div>}
        backAction={{
          onAction: () => navigate(stores.commonStore.accountSource),
        }}
        secondaryActions={upgradeStore.appType === "bundle" ? undefined : [
          {
            content: t("ApplyDiscountCode") as string,
            onAction() {
              openModalAndInitDiscountCode()
            },
          },
        ]}
      />

      {!!upgradeStore.showBannerData?.show_bundle &&
        <div className={"flex justify-center sm:hidden mb-2"}>
          <PBAppToggleComponent />
        </div>
      }

      {upgradeStore.appType === "parcelpanel" && <>
        <SwitchTab
          className="w-max-content mx-auto"
          value={upgradeStore.payType}
          radioOptions={[
            { label: t("PayMonthly"), value: AppPricingInterval.Every30_Days },
            { label: t("PayAnnuallySave20"), value: AppPricingInterval.Annual },
          ]}
          handleRadio={(value: string) => upgradeStore.setPayType(value as AppPricingInterval)}
        />
        {/* banner区域, 统一由此组件管理 */}
        <AccountBanners currentSubscription={upgradeStore.currentPlan} />

        <PlanList
          payType={upgradeStore.payType}
          currentSubscription={upgradeStore.currentPlan}
          updateCurrentSubscription={updateCurrentSubscription}
          plans={upgradeStore.plans}
          loading={loading || !upgradeStore.initialized}
          openUpgradeModal={openUpgradeModal}
          // disabledSubscription={userInfoSore.data.subscription_payment_method === 1}
          subscriptionPaymentMethod={userInfoSore.data.subscription_payment_method}
        />

        <div className="mt-4" />

        {/* 描述卡片区域*/}
        <DescCards />
      </>}

      {/* 产三 bundle套餐页 */}
      {upgradeStore.appType === "bundle" && <>
        <PBProvide language={stores.commonStore.currentLanguageCode}>
          <BundlePage />
        </PBProvide>
      </>}

      {/* 升级后显示弹窗*/}
      {/* <UpgradedSuccessModal*/}
      {/*  show={showUpgradeModal}*/}
      {/*  onClose={() => {*/}
      {/*    clearPathnameQuery("/billing/plans")*/}
      {/*    setShowUpgradeModal(false)*/}
      {/*  }}*/}
      {/* />*/}

      {/* Bundle套餐切换提示弹窗*/}
      <Modal
        title={t("ChangePlanReminder")}
        open={upgradeStore.showBundleChangePlanReminder}
        onClose={() => upgradeStore.setShowBundleChangePlanReminder(false)}
        primaryAction={{
          content: t("ChangePlan"),
          onAction: () => navigate("/integration?selected_tab=9"),
        }}
        secondaryActions={[
          { content: commonT("Close"), onAction: () => upgradeStore.setShowBundleChangePlanReminder(false) },
        ]}
      >
        <Modal.Section>
          {t("BundleChangePanReminder")}
        </Modal.Section>
      </Modal>
    </Container>
  }</Observer>
}

export default Account
